import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import HeaderBar from '@/components/HeaderBar';

const API = process.env.REACT_APP_BACKEND_URL;

// Default features for plan types (used if not in database)
const DEFAULT_FEATURES = {
    starter: [
        'One site',
        '3 years of data retention',
        'Intuitive, fast and privacy-friendly dashboard',
        'Email/Slack reports',
        'Google Analytics import',
        'Goals and custom events',
        'Saved Segments'
    ],
    growth: [
        'Everything in Starter',
        'Up to 3 sites',
        'Up to 3 team members',
        'Team Management',
        'Shared Links',
        'Embedded Dashboards',
        'Shared Segments'
    ],
    business: [
        'Everything in Growth',
        'Up to 10 sites',
        'Up to 10 team members',
        '5 years of data retention',
        'Custom Properties',
        'Stats API (600 requests per hour)',
        'Looker Studio Connector',
        'Ecommerce revenue attribution',
        'Funnels',
        'Consolidated View'
    ],
    enterprise: [
        'Everything in Business',
        '10+ sites',
        '10+ team members',
        '600+ Stats API requests per hour',
        'Sites API access for reselling',
        'Single Sign-On (SSO)',
        'Managed Proxy',
        'Scheduled raw event data exports',
        '5+ years of data retention',
        'Priority support'
    ]
};

export default function ChoosePlanPage({ token, user, logout }) {
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [tiers, setTiers] = useState([]);
    const [selectedTierIndex, setSelectedTierIndex] = useState(0);
    const [billingCycle, setBillingCycle] = useState('yearly');
    const [loading, setLoading] = useState(true);
    const [checkoutLoading, setCheckoutLoading] = useState(false);

    // Fetch plans from API
    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const response = await axios.get(`${API}/api/plans`);
                setPlans(response.data);

                // Extract unique tiers
                const uniqueTiers = {};
                response.data.forEach(plan => {
                    if (!uniqueTiers[plan.page_limit]) {
                        uniqueTiers[plan.page_limit] = plan.page_limit_label;
                    }
                });

                const sortedTiers = Object.entries(uniqueTiers)
                    .map(([pageLimit, label]) => ({
                        value: parseInt(pageLimit),
                        label: label
                    }))
                    .sort((a, b) => a.value - b.value);

                setTiers(sortedTiers);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching plans:', error);
                toast.error('Failed to load plans');
                setLoading(false);
            }
        };

        fetchPlans();
    }, []);

    const currentTier = tiers[selectedTierIndex] || { value: 10000, label: '10k' };

    // Get plan for a specific type at current tier and billing cycle
    const getPlanForType = (planType) => {
        return plans.find(
            p => p.type === planType &&
                p.page_limit === currentTier.value &&
                p.duration === billingCycle
        );
    };

    // Get price for display (monthly equivalent for yearly plans)
    const getDisplayPrice = (planType) => {
        const plan = getPlanForType(planType);
        if (!plan) return null;

        if (billingCycle === 'yearly') {
            // Show monthly equivalent
            return (plan.price / 12).toFixed(2);
        }
        return plan.price.toFixed(2);
    };

    // Get yearly total
    const getYearlyTotal = (planType) => {
        const plan = getPlanForType(planType);
        return plan ? plan.price : null;
    };

    // Get monthly price for crossed out display
    const getMonthlyPrice = (planType) => {
        const monthlyPlan = plans.find(
            p => p.type === planType &&
                p.page_limit === currentTier.value &&
                p.duration === 'monthly'
        );
        return monthlyPlan ? monthlyPlan.price : null;
    };

    // Get features for a plan type
    const getFeatures = (planType) => {
        const plan = getPlanForType(planType);
        if (plan && plan.features && plan.features.length > 0) {
            return plan.features;
        }
        return DEFAULT_FEATURES[planType] || [];
    };

    // Handle upgrade button click
    const handleUpgrade = async (planType) => {
        if (planType === 'enterprise') {
            toast.info('Please contact us for custom enterprise pricing');
            return;
        }

        const plan = getPlanForType(planType);
        if (!plan) {
            toast.error('Plan not available at this tier');
            return;
        }

        setCheckoutLoading(true);

        try {
            const response = await axios.post(
                `${API}/api/checkout/create-session`,
                {
                    plan_id: plan.id,
                    origin_url: window.location.origin
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            // Redirect to Stripe Checkout
            if (response.data.url) {
                window.location.href = response.data.url;
            }
        } catch (error) {
            console.error('Checkout error:', error);
            toast.error(error.response?.data?.detail || 'Failed to start checkout');
            setCheckoutLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
        );
    }

    // Check if plan exists for a type at current tier
    const hasPlanForType = (planType) => {
        return !!getPlanForType(planType);
    };

    return (
        <div className="settings-page">
            {/* Header Bar */}
            <HeaderBar user={user} logout={logout} />

            {/* Settings Header */}
            <div className="settings-header">
                <div className="settings-header-content settings-header-row">
                    <h1 className="settings-title">Choose a plan</h1>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/subscription')}
                        className="back-button"
                    >
                        ← Back to subscription
                    </Button>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Title */}
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                        Traffic based plans that match your growth
                    </h1>
                    <p className="text-gray-600">
                        Upgrade your trial to a paid plan
                    </p>
                </div>

                {/* Pageview Slider */}
                {tiers.length > 0 && (
                    <div className="mb-8">
                        <div className="flex items-center justify-center gap-4 mb-4">
                            <span className="text-sm text-gray-600">Up to</span>
                            <span className="bg-indigo-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                                {currentTier.label}
                            </span>
                            <span className="text-sm text-gray-600">monthly pageviews</span>
                        </div>

                        <div className="flex items-center justify-center gap-2 mb-4">
                            <span className="text-xs text-gray-500">{tiers[0]?.label}</span>
                            <input
                                type="range"
                                min="0"
                                max={tiers.length - 1}
                                value={selectedTierIndex}
                                onChange={(e) => setSelectedTierIndex(parseInt(e.target.value))}
                                className="w-96 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                            />
                            <span className="text-xs text-gray-500">{tiers[tiers.length - 1]?.label}</span>
                        </div>

                        {/* Tier Labels */}
                        <div className="flex justify-center gap-1 text-xs text-gray-400">
                            {tiers.map((tier, index) => (
                                <button
                                    key={tier.value}
                                    onClick={() => setSelectedTierIndex(index)}
                                    className={`px-2 py-1 rounded ${selectedTierIndex === index
                                        ? 'bg-indigo-100 text-indigo-700 font-medium'
                                        : 'hover:bg-gray-100'
                                        }`}
                                >
                                    {tier.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Billing Toggle */}
                <div className="flex items-center justify-center gap-4 mb-8">
                    <button
                        onClick={() => setBillingCycle('monthly')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${billingCycle === 'monthly'
                            ? 'bg-gray-200 text-gray-900'
                            : 'text-gray-600 hover:bg-gray-100'
                            }`}
                    >
                        Monthly
                    </button>
                    <button
                        onClick={() => setBillingCycle('yearly')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${billingCycle === 'yearly'
                            ? 'bg-indigo-600 text-white'
                            : 'text-gray-600 hover:bg-gray-100'
                            }`}
                    >
                        Yearly
                    </button>
                    {billingCycle === 'yearly' && (
                        <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full font-medium">
                            2 months free
                        </span>
                    )}
                </div>

                {/* Pricing Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* Starter Plan */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Starter</h3>

                        {hasPlanForType('starter') ? (
                            <div className="mb-4">
                                <span className="text-3xl font-bold text-gray-900">
                                    ${getDisplayPrice('starter')}
                                </span>
                                <span className="text-gray-500 text-sm">/month</span>
                                {billingCycle === 'yearly' && (
                                    <>
                                        <div className="text-xs text-gray-400 line-through">
                                            ${getMonthlyPrice('starter')}/month
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            ${getYearlyTotal('starter')}/year
                                        </div>
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="mb-4">
                                <span className="text-2xl font-bold text-gray-400">Not available</span>
                            </div>
                        )}

                        <Button
                            onClick={() => handleUpgrade('starter')}
                            disabled={!hasPlanForType('starter') || checkoutLoading}
                            className="w-full mb-6 bg-indigo-600 hover:bg-indigo-700"
                        >
                            {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Upgrade'}
                        </Button>

                        <ul className="space-y-3">
                            {getFeatures('starter').map((feature, index) => (
                                <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                                    <Check className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                                    {feature}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Growth Plan */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Growth</h3>

                        {hasPlanForType('growth') ? (
                            <div className="mb-4">
                                <span className="text-3xl font-bold text-gray-900">
                                    ${getDisplayPrice('growth')}
                                </span>
                                <span className="text-gray-500 text-sm">/month</span>
                                {billingCycle === 'yearly' && (
                                    <>
                                        <div className="text-xs text-gray-400 line-through">
                                            ${getMonthlyPrice('growth')}/month
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            ${getYearlyTotal('growth')}/year
                                        </div>
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="mb-4">
                                <span className="text-2xl font-bold text-gray-400">Not available</span>
                            </div>
                        )}

                        <Button
                            onClick={() => handleUpgrade('growth')}
                            disabled={!hasPlanForType('growth') || checkoutLoading}
                            className="w-full mb-6 bg-indigo-600 hover:bg-indigo-700"
                        >
                            {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Upgrade'}
                        </Button>

                        <ul className="space-y-3">
                            {getFeatures('growth').map((feature, index) => (
                                <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                                    <Check className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                                    {feature}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Business Plan - Recommended */}
                    <div className="bg-white rounded-xl border-2 border-indigo-500 p-6 hover:shadow-lg transition-shadow relative">
                        <span className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-indigo-100 text-indigo-700 text-xs px-3 py-1 rounded-full font-medium">
                            Recommended
                        </span>

                        <h3 className="text-lg font-semibold text-indigo-600 mb-4">Business</h3>

                        {hasPlanForType('business') ? (
                            <div className="mb-4">
                                <span className="text-3xl font-bold text-gray-900">
                                    ${getDisplayPrice('business')}
                                </span>
                                <span className="text-gray-500 text-sm">/month</span>
                                {billingCycle === 'yearly' && (
                                    <>
                                        <div className="text-xs text-gray-400 line-through">
                                            ${getMonthlyPrice('business')}/month
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            ${getYearlyTotal('business')}/year
                                        </div>
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="mb-4">
                                <span className="text-2xl font-bold text-gray-400">Coming soon</span>
                            </div>
                        )}

                        <Button
                            onClick={() => handleUpgrade('business')}
                            disabled={!hasPlanForType('business') || checkoutLoading}
                            className="w-full mb-6 bg-indigo-600 hover:bg-indigo-700"
                        >
                            {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : hasPlanForType('business') ? 'Upgrade' : 'Coming soon'}
                        </Button>

                        <ul className="space-y-3">
                            {getFeatures('business').map((feature, index) => (
                                <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                                    <Check className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                                    {feature}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Enterprise Plan */}
                    <div className="bg-gray-900 rounded-xl p-6 hover:shadow-lg transition-shadow text-white">
                        <h3 className="text-lg font-semibold mb-4">Enterprise</h3>

                        <div className="mb-4">
                            <span className="text-3xl font-bold">Custom</span>
                        </div>

                        <Button
                            onClick={() => handleUpgrade('enterprise')}
                            variant="secondary"
                            className="w-full mb-6 bg-gray-700 hover:bg-gray-600 text-white border-0"
                        >
                            Contact us
                        </Button>

                        <ul className="space-y-3">
                            {getFeatures('enterprise').map((feature, index) => (
                                <li key={index} className="flex items-start gap-2 text-sm text-gray-300">
                                    <Check className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
                                    {feature}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* FAQ Section */}
                <div className="mt-12 max-w-3xl mx-auto">
                    <div className="bg-white rounded-xl border border-gray-200 divide-y">
                        <details className="p-4 cursor-pointer">
                            <summary className="font-medium text-gray-900 flex justify-between items-center">
                                What&apos;s my current usage?
                                <span className="text-gray-400">−</span>
                            </summary>
                            <p className="mt-2 text-sm text-gray-600">
                                You have used <strong>0</strong> billable pageviews in the last 30 days.
                                Please see your full usage report (including sites and team members) under
                                the <Link to="/settings/subscription" className="text-indigo-600 hover:underline">Subscription</Link> section
                                in your account settings.
                            </p>
                        </details>

                        <details className="p-4 cursor-pointer">
                            <summary className="font-medium text-gray-900 flex justify-between items-center">
                                What happens if I go over my monthly pageview limit?
                                <span className="text-gray-400">+</span>
                            </summary>
                            <p className="mt-2 text-sm text-gray-600">
                                We won&apos;t stop tracking your data if you go over your limit. We&apos;ll send you a
                                friendly reminder to upgrade your plan. If you consistently exceed your limit,
                                we&apos;ll work with you to find the right plan for your needs.
                            </p>
                        </details>

                        <details className="p-4 cursor-pointer">
                            <summary className="font-medium text-gray-900 flex justify-between items-center">
                                Can I change my plan later?
                                <span className="text-gray-400">+</span>
                            </summary>
                            <p className="mt-2 text-sm text-gray-600">
                                Yes! You can upgrade or downgrade your plan at any time. If you upgrade,
                                we&apos;ll prorate the difference. If you downgrade, the new rate will apply
                                at the start of your next billing cycle.
                            </p>
                        </details>

                        <details className="p-4 cursor-pointer">
                            <summary className="font-medium text-gray-900 flex justify-between items-center">
                                Do you offer refunds?
                                <span className="text-gray-400">+</span>
                            </summary>
                            <p className="mt-2 text-sm text-gray-600">
                                Yes, we offer a 30-day money-back guarantee on all plans. If you&apos;re not
                                satisfied, contact us within 30 days of your purchase for a full refund.
                            </p>
                        </details>
                    </div>
                </div>

                {/* Current Plan Limit Notice */}
                <div className="mt-8 text-center text-sm text-gray-500">
                    <p>Every account has a limit of <strong>10,000 pageviews</strong> per month on the free tier.</p>
                </div>
            </div>
        </div>
    );
}
